import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  RefreshControl,
  FlatList,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import DraggableFlatList from 'react-native-draggable-flatlist';
import { GestureHandlerRootView } from 'react-native-gesture-handler';

// Check if running on web
const isWeb = Platform.OS === 'web';
import {
  getShoppingListByStore,
  purchaseItem,
  deleteItem,
  clearPurchasedItems,
  reorderItems,
} from '../services/shoppingService';

const StoreDetailScreen = ({ route, navigation }) => {
  const { storeId, storeName } = route.params;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useFocusEffect(
    useCallback(() => {
      loadItems();
    }, [storeId])
  );

  const loadItems = async () => {
    const result = await getShoppingListByStore(storeId);
    
    if (result.success) {
      setItems(result.data.items || []);
    } else {
      Alert.alert('Fehler', result.error);
    }
    
    setLoading(false);
    setRefreshing(false);
  };

  const onRefresh = () => {
    setRefreshing(true);
    loadItems();
  };

  const handleTogglePurchase = async (item) => {
    const result = await purchaseItem(item.id, !item.is_purchased);
    
    if (result.success) {
      loadItems(); // Neu laden um Sortierung zu aktualisieren
    } else {
      Alert.alert('Fehler', result.error);
    }
  };

  const handleDeleteItem = (item) => {
    Alert.alert(
      'Artikel entfernen',
      `"${item.article_name}" von der Liste entfernen?`,
      [
        { text: 'Abbrechen', style: 'cancel' },
        {
          text: 'Entfernen',
          style: 'destructive',
          onPress: async () => {
            const result = await deleteItem(item.id);
            if (result.success) {
              loadItems();
            } else {
              Alert.alert('Fehler', result.error);
            }
          },
        },
      ]
    );
  };

  const handleClearPurchased = () => {
    const purchasedCount = items.filter(item => item.is_purchased).length;
    
    if (purchasedCount === 0) {
      Alert.alert('Info', 'Keine gekauften Artikel vorhanden');
      return;
    }

    Alert.alert(
      'Gekaufte Artikel entfernen',
      `${purchasedCount} gekaufte ${purchasedCount === 1 ? 'Artikel' : 'Artikel'} von der Liste entfernen?`,
      [
        { text: 'Abbrechen', style: 'cancel' },
        {
          text: 'Entfernen',
          style: 'destructive',
          onPress: async () => {
            const result = await clearPurchasedItems(storeId);
            if (result.success) {
              loadItems();
            } else {
              Alert.alert('Fehler', result.error);
            }
          },
        },
      ]
    );
  };

  const handleDragEnd = async ({ data }) => {
    // Update lokale State
    setItems(data);
    
    // Update auf Server
    const reorderedItems = data.map((item, index) => ({
      id: item.id,
      sort_order: index,
    }));
    
    await reorderItems(storeId, reorderedItems);
  };

  const renderItem = ({ item, drag, isActive }) => {
    return (
      <View 
        style={[
          styles.itemCard,
          isActive && styles.itemCardDragging,
          item.is_purchased && styles.itemCardPurchased,
        ]}
      >
        <TouchableOpacity
          style={styles.checkboxContainer}
          onPress={() => handleTogglePurchase(item)}
        >
          <Ionicons
            name={item.is_purchased ? 'checkmark-circle' : 'ellipse-outline'}
            size={28}
            color={item.is_purchased ? '#4CAF50' : '#999'}
          />
        </TouchableOpacity>

        <View style={styles.itemContent}>
          <Text 
            style={[
              styles.itemName,
              item.is_purchased && styles.itemNamePurchased
            ]}
          >
            {item.article_name}
          </Text>
          
          <View style={styles.itemDetails}>
            <Text style={styles.itemQuantity}>
              {item.quantity} {item.unit}
            </Text>
            {item.tags && item.tags.length > 0 && (
              <View style={styles.tagsContainer}>
                {item.tags.slice(0, 2).map((tag, index) => (
                  <View key={index} style={styles.tag}>
                    <Text style={styles.tagText}>{tag}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
          
          {item.notes && (
            <Text style={styles.itemNotes} numberOfLines={1}>
              {item.notes}
            </Text>
          )}
        </View>

        {!isWeb && (
          <TouchableOpacity
            style={styles.dragHandle}
            onLongPress={drag}
          >
            <Ionicons name="reorder-two" size={24} color="#999" />
          </TouchableOpacity>
        )}

        <TouchableOpacity
          style={styles.deleteButton}
          onPress={() => handleDeleteItem(item)}
        >
          <Ionicons name="trash-outline" size={20} color="#d32f2f" />
        </TouchableOpacity>
      </View>
    );
  };
  
  // Simple render for web (without drag)
  const renderItemSimple = ({ item }) => renderItem({ item, drag: () => {}, isActive: false });

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#2E7D32" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.addButton}
          onPress={() => navigation.navigate('AddItem', { storeId, storeName })}
        >
          <Ionicons name="add-circle" size={20} color="#fff" />
          <Text style={styles.addButtonText}>Artikel hinzufügen</Text>
        </TouchableOpacity>

        {items.some(item => item.is_purchased) && (
          <TouchableOpacity
            style={styles.clearButton}
            onPress={handleClearPurchased}
          >
            <Ionicons name="trash-outline" size={18} color="#666" />
          </TouchableOpacity>
        )}
      </View>

      {items.length === 0 ? (
        <View style={styles.centerContainer}>
          <Ionicons name="cart-outline" size={64} color="#ccc" />
          <Text style={styles.emptyText}>Keine Artikel</Text>
          <Text style={styles.emptySubtext}>
            Füge Artikel zu dieser Liste hinzu
          </Text>
        </View>
      ) : isWeb ? (
        // Simple FlatList for web (no drag & drop)
        <FlatList
          data={items}
          renderItem={renderItemSimple}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl 
              refreshing={refreshing} 
              onRefresh={onRefresh}
              colors={['#2E7D32']}
            />
          }
        />
      ) : (
        // DraggableFlatList for native (with drag & drop)
        <GestureHandlerRootView style={{ flex: 1 }}>
          <DraggableFlatList
            data={items}
            renderItem={renderItem}
            keyExtractor={item => item.id}
            onDragEnd={handleDragEnd}
            contentContainerStyle={styles.listContent}
            refreshControl={
              <RefreshControl 
                refreshing={refreshing} 
                onRefresh={onRefresh}
                colors={['#2E7D32']}
              />
            }
          />
        </GestureHandlerRootView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    padding: 15,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    alignItems: 'center',
  },
  addButton: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#2E7D32',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  clearButton: {
    padding: 12,
    marginLeft: 10,
  },
  listContent: {
    padding: 15,
  },
  itemCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 15,
    marginBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  itemCardDragging: {
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  itemCardPurchased: {
    backgroundColor: '#f0f7f0',
    borderLeftWidth: 4,
    borderLeftColor: '#4CAF50',
  },
  checkboxContainer: {
    marginRight: 12,
  },
  itemContent: {
    flex: 1,
  },
  itemName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  itemNamePurchased: {
    textDecorationLine: 'line-through',
    color: '#999',
  },
  itemDetails: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  itemQuantity: {
    fontSize: 14,
    color: '#666',
    marginRight: 10,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  tag: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    marginRight: 5,
  },
  tagText: {
    fontSize: 11,
    color: '#2E7D32',
    fontWeight: '600',
  },
  itemNotes: {
    fontSize: 12,
    color: '#999',
    marginTop: 4,
    fontStyle: 'italic',
  },
  dragHandle: {
    padding: 8,
    marginLeft: 8,
  },
  deleteButton: {
    padding: 8,
    marginLeft: 4,
  },
  emptyText: {
    fontSize: 20,
    color: '#999',
    marginTop: 20,
    fontWeight: '600',
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 10,
  },
});

export default StoreDetailScreen;

